import { ChangeDetectorRef, Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { defer } from 'rxjs';
import { tap } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { IconTextComponent, SimpleButtonComponent, SimpleInputComponent, TitleContentComponent } from 'ngx-zcs-components';

import { ChooseState } from '../create-block/create-block.model';
import { ChoiceWithImgComponent } from '../components/choice-with-img/choice-with-img.component';
import { getConnextErrorMessage, isConnextLoggerSelected } from '../create-firebase/create.utils';
import { CreateFirebaseBlockService } from '../create-firebase/create-firebase-block.service';

export type MonitoringForm = FormGroup<{
  monitoringPreference: FormControl<ChooseState[] | null>;
  connextSerial: FormControl<string | null>;
  azzurroHubSerial: FormControl<string | null>;
  importSystemName: FormControl<string | null>;
}>;

export type BaseMonitoringForm = FormGroup<{
  monitoringPreference: FormControl<ChooseState[] | null>;
  connextSerial: FormControl<string | null>;
  importSystemName: FormControl<string | null>;
}>;

const getLabels = () => ({
  alreadyInserted: 'Already inserted',
  alreadyInsertedDescription: 'The selected logger is already inserted in the system',
  checkAgain: 'Check again',
  chooseLogger: 'Choose Logger',
  connext: 'Connext',
  connextPlaceholder: 'E.g. CA...',
  connextSerial: 'Connext Serial',
  azzurroHub: 'Azzurro Hub',
  azzurroHubPlaceholder: 'E.g. AH1234567890',
  azzurroHubSerial: 'Azzurro Hub Serial',
  firmwareNotValidDescription: 'An upgrade is required to continue.',
  azzurroHubBusyDescription: 'Azzurro Hub is busy, please try again later',
  firmwareOperationDescription: 'Firmware update in progress, please wait a few minutes',
  fwUpdateContent: 'The firmware update is in progress. Please wait a few minutes.',
  fwUpdateTitle: 'Firmware Update',
  import: 'Import',
  offlineDevice: 'Offline device',
  offlineDeviceDescription: 'The device is offline. To continue, the device needs to be online.',
  others: 'Others',
  plantAlreadyCreated: 'The plant has already been created',
  plantAlreadyCreatedDescription: 'You can add the plant with view-only rights',
  requestingFirmwareUpdate: 'Requesting firmware update',
  requiredOperation: 'Required operation',
  searchSystem: 'Search system',
  systemName: 'System name',
  systemNamePlaceholder: 'E.g. Giovanni House',
  upgradeFirmware: 'Upgrade Firmware',

	azzurroHubBusy: 'Azzurro Hub is busy',
	deviceNotExists: 'Device does not exist',
	deviceNotOnline: 'Device is not online',
	fwUpgradeInProgress: 'Firmware upgrade in progress',
	fwVersionNotValidUpgradeRequired: 'Firmware version not valid, upgrade required',
	hasOwnerAndItsNotYou: 'Device has an owner and it is not you',
	iAmAlreadyAdmin: 'I am already an admin',
	serialNotValid: 'Serial not valid',
	validationError: 'Validation error',
  deviceAlreadyExistsInYourSystemList: 'Device already exists in your system list',
  deviceAlreadyOnACertifiedPlant: 'Device already on a certified plant',
});

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ChoiceWithImgComponent,
    forwardRef(() => IconTextComponent),
    MatIconModule,
    forwardRef(() => SimpleButtonComponent),
    forwardRef(() => SimpleInputComponent),
    forwardRef(() => TitleContentComponent),
    TranslateModule,
  ],
  selector: 'customer-monitoring-section',
  templateUrl: './monitoring-section.component.html',
  styleUrls: ['./monitoring-section.component.scss'],
})
export class MonitoringSectionComponent {
  private _monitoringForm!: MonitoringForm | BaseMonitoringForm;
  @Input() set monitoringForm(value: MonitoringForm | BaseMonitoringForm) {
    this._monitoringForm = value;
    value.controls.monitoringPreference.setValue(this.loggers);
  }
  get monitoringForm() {
    return this._monitoringForm;
  }

  readonly robustnessOnFormInput$ = defer(() => this.monitoringForm.controls.connextSerial.statusChanges).pipe(
    tap(() => this.changeDetectorRef.detectChanges()),
  );

  readonly robustnessOnAzzurroHubFormInput$ = defer(() => {
    const azzurroHubControl = (this.monitoringForm as any).controls.azzurroHubSerial;
    if (azzurroHubControl) {
      // Subscribe to value changes to automatically convert to uppercase
      azzurroHubControl.valueChanges.subscribe((value: string | null) => {
        if (value && typeof value === 'string' && value !== value.toUpperCase()) {
          // Prevent infinite loop by checking if conversion is needed
          azzurroHubControl.setValue(value.toUpperCase(), { emitEvent: false });
        }
      });
      return azzurroHubControl.statusChanges;
    } else {
      return this.monitoringForm.controls.connextSerial.statusChanges;
    }
  }).pipe(
    tap(() => this.changeDetectorRef.detectChanges()),
  );

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  @Input() loggers: ChooseState[] = [];
  @Input() enableImportSystem = true;

  @Output() readonly changeLogger = new EventEmitter<void>();
  @Output() wizardFinished = new EventEmitter<string>();

  get isConnextLoggerSelected() {
    return isConnextLoggerSelected(this.monitoringPreference?.value);
  }

  get isAzzurroHubLoggerSelected() {
    return this.monitoringPreference?.value?.some((logger) => logger.name === 'azzurroHub' && logger.checked$.value) ?? false;
  }

  get hasAzzurroHubSerial() {
    return !!(this.monitoringForm as any).controls.azzurroHubSerial;
  }

  get connextErrorMessage() {
    return getConnextErrorMessage(this.labels, this.connextSerial?.errors);
  }

  get azzurroHubErrorMessage() {
    return getConnextErrorMessage(this.labels, this.azzurroHubSerial?.errors); // TODO: Create getAzzurroHubErrorMessage
  }

  get connextSerial() {
    return this.monitoringForm.controls.connextSerial;
  }

  get azzurroHubSerial() {
    return (this.monitoringForm as any).controls.azzurroHubSerial;
  }

  get monitoringPreference() {
    return this.monitoringForm.controls.monitoringPreference;
  }

  get importSystemName() {
    return this.monitoringForm.controls.importSystemName;
  }

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    readonly createFirebaseBlockService: CreateFirebaseBlockService,
    private readonly router: Router,
  ) {
    this.updateValueAndValidity = this.updateValueAndValidity.bind(this);
  }

  searchSystem(text: string) {
    return async () => {
      await this.router.navigate([], {
        queryParams: { search: text },
        queryParamsHandling: 'merge',
      });
    };
  }

  clearInput() {
    this.router.navigate([], {
      queryParams: { search: null },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Ensures the Azzurro Hub serial is always uppercase
   */
  private ensureAzzurroHubSerialUppercase(): void {
    if (this.hasAzzurroHubSerial) {
      const currentValue = this.azzurroHubSerial.value;
      if (currentValue && typeof currentValue === 'string' && currentValue !== currentValue.toUpperCase()) {
        this.azzurroHubSerial.setValue(currentValue.toUpperCase(), { emitEvent: false });
      }
    }
  }

  updateValueAndValidity() {
    this.monitoringForm.updateValueAndValidity();
    this.connextSerial.updateValueAndValidity();
    if (this.hasAzzurroHubSerial) {
      // Ensure Azzurro Hub serial is uppercase before validation
      this.ensureAzzurroHubSerialUppercase();
      this.azzurroHubSerial.updateValueAndValidity();
    }
  }

  createPlant(plantName: string, connextId: string) {
    return async () => {
      const response = await this.createFirebaseBlockService.createFirebasePlant(plantName, { connextId })();
      if (response.success?.uid) {
        this.wizardFinished.emit(response.success?.uid);
      }
    }
  }
}
